import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { SmartProgressBar } from '@app/components/progress/SmartProgressBar';
import { Routes } from '@app/constants/routes';
import { useActivePlan } from '@app/domain/plan/hooks/useActivePlan';
import { useCurrentTeam } from '@app/domain/team/hooks/useCurrentTeam';
import { TypedLink } from '@app/router';
import { capitalizeWords } from '@app/utils/string.utils';
import { useTranslation } from 'react-i18next';

export const CustomerNavSidebarPlanStatus: React.FC = () => {
  const { t } = useTranslation();
  const { isOldPlanUser, currentUser: user } = useAuthorizedUser();
  const { planType, isTrial } = useActivePlan();

  const { isVisibleToAllOrOwnerOnly } = useCurrentTeam();

  if (isOldPlanUser || !user || isTrial) {
    return null;
  }

  const totalScans = user?.monthlyScans || 0;
  const maxScans = user?.currentPlanDetails?.amountMaxScans || 0;
  const totalCodes = user?.totalCodes || 0;
  const amountMaxQrCodes = user?.currentPlanDetails?.amountMaxQrCodes || 0;

  return (
    <div className="bg-slate-400/50 text-white p-4 rounded-t-lg flex flex-col gap-4">
      <div className="flex gap-2">
        <div className="font-semibold">{capitalizeWords(planType || '')}</div>
        <div>{t('billing.plan')}</div>
        {isVisibleToAllOrOwnerOnly && (
          <TypedLink to={Routes.customer.account.subscription} className="text-s underline ml-auto">
            {t('subscription.buttonManagePlan')}
          </TypedLink>
        )}
      </div>
      {!isOldPlanUser && (
        <>
          <UsageBlock
            title={t('subscription.qrCodesCreated')}
            current={totalCodes}
            max={amountMaxQrCodes}
            progressValue={totalCodes > amountMaxQrCodes ? amountMaxQrCodes : totalCodes}
            progressMax={amountMaxQrCodes}
            testId="usage-block-qr-total-codes"
          />
          <hr className="border-dashed border-white/15" />
        </>
      )}
      <UsageBlock
        title={t('subscription.qrScans')}
        current={totalScans}
        max={maxScans}
        progressValue={totalScans > maxScans ? maxScans : totalScans}
        progressMax={maxScans}
        testId="usage-block-qr-scans"
      />
      <NextUsageReset newUsageResetDate={user.newUsageResetDate} />
    </div>
  );
};

type UsageBlockProps = {
  title: string;
  current: number;
  max?: null | number;
  progressValue?: number;
  progressMax?: null | number;
  testId: string;
};

const UsageBlock: React.FC<UsageBlockProps> = ({ title, current, max, progressValue, progressMax, testId }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex justify-between">
        <div>{title}</div>
        <div className="flex gap-2" data-testid={testId}>
          <b data-testid="current-value">{current}</b>
          <div>{t('subscription.ofAmount')}</div>
          <b>{max || `∞`}</b>
        </div>
      </div>
      <div>
        <SmartProgressBar value={progressMax ? progressValue : -1} max={progressMax || 100} />
      </div>
    </>
  );
};

const NextUsageReset: React.FC<{
  newUsageResetDate?: string | null;
}> = ({ newUsageResetDate }) => {
  const { t } = useTranslation();

  if (!newUsageResetDate) {
    return null;
  }

  try {
    const now = Date.now();
    const futureTime = new Date(newUsageResetDate).getTime();
    const deltaTime = futureTime - now;
    const daysLeft = Math.round(deltaTime / DAY_IN_MS);
    return <div className="text-xs">{t('limit.usageResetsInDays', { count: daysLeft })}</div>;
  } catch (e) {
    console.error('invalid usage reset date', e);
    return null;
  }
};

const DAY_IN_MS = 60e3 * 60 * 24;
